/**
 * Who a session belongs to, rendered the same way everywhere it appears.
 *
 * The app tells us two things through `identify()`: its own id for the person
 * and, optionally, a display name. The name leads because that is what an agent
 * on a call has in front of them; the id stays visible underneath because it is
 * what the customer's own systems use and what search takes. With no
 * name there is only the id, and with neither the session is anonymous — a real
 * state worth showing, not a blank cell.
 */

export interface EndUserProps {
  id?: string;
  name?: string;
  /** City and state, already formatted. Shown under the name when present. */
  place?: string;
  /** Show the round monogram. Off in dense tables. */
  avatar?: boolean;
}

/**
 * Two letters for the monogram, from the name when there is one, otherwise from
 * the id. "Jane Doe" and "jane.doe@acme.com" both give JD; a single-part value
 * falls back to its first two characters, as do the `u_`/`u_hash_` identifiers
 * that sessions recorded before readable identity carry.
 */
export function endUserInitials(id: string, name?: string): string {
  const source = name?.trim() || id.replace(/^u_(hash_)?/, "");
  const parts = source.split(/[^a-zA-Z0-9]+/).filter(Boolean);
  const letters = parts.length > 1 ? parts[0]![0]! + parts[1]![0]! : (parts[0] ?? source).slice(0, 2);
  return letters.toUpperCase();
}

function PlaceLine({ place }: { place?: string }) {
  const text = place?.trim();
  if (!text) return null;
  return (
    <span className="end-user-place" title={text}>
      {text}
    </span>
  );
}

export function EndUser({ id, name, place, avatar }: EndUserProps) {
  if (!id) {
    return (
      <span className="end-user">
        {avatar && (
          <span className="session-avatar anon" aria-hidden="true">
            ?
          </span>
        )}
        {place?.trim() ? (
          <span className="end-user-ident">
            <span className="muted">Anonymous</span>
            <PlaceLine place={place} />
          </span>
        ) : (
          <span className="muted">Anonymous</span>
        )}
      </span>
    );
  }
  return (
    <span className="end-user">
      {avatar && (
        <span className="session-avatar" aria-hidden="true">
          {endUserInitials(id, name)}
        </span>
      )}
      <span className="end-user-ident">
        {name ? (
          <>
            <span className="end-user-name" title={name}>
              {name}
            </span>
            <PlaceLine place={place} />
            <span className="end-user-id mono" title={id}>
              {id}
            </span>
          </>
        ) : (
          <>
            <span className="end-user-name mono" title={id}>
              {id}
            </span>
            <PlaceLine place={place} />
          </>
        )}
      </span>
    </span>
  );
}
